import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../containers/UseAuth';
import { useService } from '../../../containers/UseService';
import { useForecast } from '../../../contexts/ForecastContext';
import Audiences from './Blocks/Audiences';
import Basics from './Blocks/Basics';
import Location from './Blocks/Location';
import Screens from './Blocks/Screens';
import {
    aggregateForecastSchedules,
    resolveForecastRange,
} from '../WizardAudiences/ForecastMetrics/forecastDistribution';
import CampaignReady from './CampaignReady/CampaignReady';
import ValidateWithProposal from './ValidateWithProposal/ValidateWithProposal';

function WizardReview({
    isActive,
    newCampaign,
    dispatch,
    userMargin,
    setCalculatingForecast,
    basics,
    location,
    screensState,
    isLaunchBlockedByActiveCampaign,
}) {
    const { t } = useTranslation();
    const service = useService();
    const [isLoading, setIsLoading] = useState(null);
    const [forecastStatus, setForecastStatus] = useState('idle');
    const [retryVersion, setRetryVersion] = useState(0);
    const latestForecastRequest = useRef('');
    const serviceRef = useRef(service);
    serviceRef.current = service;
    const auth = useAuth();
    const [backupValidations, setBackupValidations] = useState(false);
    const {
        forecast,
        setForecast,
        setForecastTable,
        forecastRequestKey: storedForecastRequestKey,
        setForecastRequestKey: setStoredForecastRequestKey,
        generalDemographic,
    } = useForecast();

    const buildSelectedLocations = (selectedScreens) => {
        const selectedCountries = new Set();
        const selectedRegions = new Set();
        const selectedCities = new Set();

        selectedScreens.forEach((screen) => {
            const geoCity = screen?.geo?.geo_city;
            if (!geoCity) return;
            selectedCities.add(
                geoCity?.metropolitan_area
                    ? geoCity.metropolitan_area?.name
                    : geoCity?.name
            );
            selectedRegions.add(geoCity?.geo_level1?.name);
            selectedCountries.add(geoCity?.geo_level1?.geo_country?.name);
        });

        return {
            selectedCountries: Array.from(selectedCountries),
            selectedRegions: Array.from(selectedRegions),
            selectedCities: Array.from(selectedCities),
        };
    };

    const selectedLocations = useMemo(() => {
        return buildSelectedLocations(screensState.selectedScreens);
    }, [screensState.selectedScreens]);

    const selectedScreens = useMemo(
        () => {
            return screensState.selectedScreens.length
                ? screensState.selectedScreens
                : newCampaign?.selectedScreens || [];
        },
        [screensState.selectedScreens, newCampaign?.selectedScreens]
    );
    const campaignForForecast = useMemo(
        () => {
            return {
                ...newCampaign,
                ...basics,
                budget: basics?.budget ?? newCampaign?.budget,
                timing: basics?.timing ?? newCampaign?.timing,
                selectedCurrency:
                    basics?.selectedCurrency ?? newCampaign?.selectedCurrency,
                selectedScreens,
            };
        },
        [newCampaign, basics, selectedScreens]
    );
    const forecastRequestKey = useMemo(
        () => {
            return serviceRef.current.getForecastRequestKey(
                campaignForForecast,
                { selectedScreens, generalDemographic }
            );
        },
        [campaignForForecast, selectedScreens, generalDemographic]
    );
    const hasUsableForecast =
        Array.isArray(forecast?.schedules) && forecast.schedules.length > 0;

    // This useEffect is necessary when the Audience component is calculating the forecast.
    // setCalculatingForecast does not allow the user to move between tabs in the wizard.
    useEffect(() => {
        if (isLoading === true) {
            setCalculatingForecast(true);
        }

        if (isLoading === false) {
            setCalculatingForecast(false);
        }
    }, [isLoading]);

    useEffect(() => {
        if (!isActive) {
            latestForecastRequest.current = '';
            setForecastStatus('idle');
            setIsLoading(false);
            return undefined;
        }

        if (
            storedForecastRequestKey === forecastRequestKey &&
            hasUsableForecast
        ) {
            latestForecastRequest.current = forecastRequestKey;
            setForecastStatus('success');
            setIsLoading(false);
            return undefined;
        }

        const requestId = `${forecastRequestKey}:${retryVersion}`;
        let isCurrent = true;
        latestForecastRequest.current = requestId;
        setForecastStatus('loading');
        setIsLoading(true);

        serviceRef.current
            .calculateForecast(campaignForForecast, {
                selectedScreens,
                generalDemographic,
            })
            .then((result) => {
                if (
                    !isCurrent ||
                    latestForecastRequest.current !== requestId
                ) {
                    return;
                }

                if (!result?.metrics) {
                    setForecastStatus('error');
                    return;
                }

                setForecast(result.metrics);
                setForecastTable(result.distributionRows || []);
                const isUsable =
                    Array.isArray(result.metrics.schedules) &&
                    result.metrics.schedules.length > 0;
                if (isUsable) {
                    setStoredForecastRequestKey(
                        result.requestKey || forecastRequestKey
                    );
                }
                setForecastStatus(isUsable ? 'success' : 'empty');
            })
            .catch(() => {
                if (
                    isCurrent &&
                    latestForecastRequest.current === requestId
                ) {
                    setForecastStatus('error');
                }
            })
            .finally(() => {
                if (
                    isCurrent &&
                    latestForecastRequest.current === requestId
                ) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [
        isActive,
        forecastRequestKey,
        storedForecastRequestKey,
        hasUsableForecast,
        retryVersion,
    ]);

    const forecastPoints = useMemo(() => {
        const schedules = Array.isArray(forecast?.schedules)
            ? forecast.schedules
            : [];
        const range = resolveForecastRange(
            campaignForForecast?.timing?.periodValue,
            schedules
        );
        return range ? aggregateForecastSchedules(schedules, range) : [];
    }, [forecast?.schedules, campaignForForecast?.timing?.periodValue]);

    useEffect(async () => {
        if (newCampaign.stepper?.wizard === 'review' && newCampaign?.label_id) {
            if (auth.isSupervising()) {
                const validations = await service.checkSubCampaignBeforeLaunch(
                    newCampaign.id
                );
                setBackupValidations(validations);
            }
        }
    }, [newCampaign, auth]);

    return (
        <>
            {!isActive ? (
                <></>
            ) : (
                <div
                    className={`basics mt-2 h-full overflow-visible overflow-y-auto text-sm transition-transform duration-299`}>
                    <div className="module rounded m-1 mb-4">
                        <div className="header flex justify-between content-center">
                            <div className="title text-xl font-extralight">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-review.review.title'
                                )}
                            </div>
                            {
                                <div
                                    className={`brief font-light text-xs text-gray-400 mt-1.5 text-right `}>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-review.review.info'
                                    )}
                                </div>
                            }
                        </div>
                        <div className="body rounded-sm p-1 divide-y divide-dotted">
                            <CampaignReady
                                dispatch={dispatch}
                                basics={basics}
                                location={location}
                                screensState={screensState}
                                isLaunchBlockedByActiveCampaign={
                                    isLaunchBlockedByActiveCampaign
                                }
                                forecastStatus={forecastStatus}
                                forecastPoints={forecastPoints}
                                forecastCurrencySymbol={
                                    campaignForForecast?.selectedCurrency
                                        ?.symbol || ''
                                }
                                onRetryForecast={() => {
                                    setRetryVersion((version) => {
                                        return version + 1;
                                    });
                                }}
                            />

                            {auth?.isSupervising() &&
                            newCampaign?.label_id &&
                            backupValidations ? (
                                <ValidateWithProposal
                                    errors={backupValidations}
                                    t={t}
                                />
                            ) : (
                                <div></div>
                            )}
                            {/* Blocks */}

                            <Basics basics={basics} />
                            <Location
                                selectedLocations={selectedLocations}
                                zones={location?.zones ?? []}
                            />
                            <Screens
                                userMargin={userMargin}
                                screensState={screensState}
                                selectedCurrency={basics?.selectedCurrency}
                            />
                            <Audiences
                                isLoading={isLoading}
                                campaign={newCampaign}
                                selectedCurrency={
                                    campaignForForecast?.selectedCurrency
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

WizardReview.proptyTypes = {
    isActive: PropTypes.bool.isRequired,
    newCampaign: PropTypes.object,
    dispatch: PropTypes.func,
    budgets: PropTypes.object,
    isLaunchBlockedByActiveCampaign: PropTypes.bool,
};

export default WizardReview;
